import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import {
  Plus, Calendar, Search, LayoutGrid, GanttChart, Play, Clock, CheckCircle2,
  PauseCircle, XCircle, ChevronDown, ChevronRight, AlertTriangle, Users as UsersIcon,
  Pencil, Trash2, Settings2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, parseISO, differenceInDays, addDays, startOfMonth, endOfMonth, min as dateMin, max as dateMax } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import UserAvatar from '../components/UserAvatar';
import MultiUserSelect from '../components/MultiUserSelect';
import ProjectTaskTemplatesDialog from '../components/ProjectTaskTemplatesDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const COLOR_MAP = {
  sky:     { bg: 'bg-sky-50',     ring: 'border-sky-200',    text: 'text-sky-700',    bar: 'bg-sky-500',    swatch: '#0EA5E9' },
  emerald: { bg: 'bg-emerald-50', ring: 'border-emerald-200', text: 'text-emerald-700', bar: 'bg-emerald-500', swatch: '#10B981' },
  violet:  { bg: 'bg-violet-50',  ring: 'border-violet-200', text: 'text-violet-700', bar: 'bg-violet-500', swatch: '#8B5CF6' },
  rose:    { bg: 'bg-rose-50',    ring: 'border-rose-200',   text: 'text-rose-700',   bar: 'bg-rose-500',   swatch: '#F43F5E' },
  amber:   { bg: 'bg-amber-50',   ring: 'border-amber-200',  text: 'text-amber-700',  bar: 'bg-amber-500',  swatch: '#F59E0B' },
  slate:   { bg: 'bg-slate-100',  ring: 'border-slate-200',  text: 'text-slate-700',  bar: 'bg-slate-500',  swatch: '#64748B' },
};

const STATUS_META = {
  active:    { key: 'active',    color: 'text-emerald-700 bg-emerald-100', icon: Play,        dot: 'bg-emerald-500' },
  planned:   { key: 'planned',   color: 'text-sky-700 bg-sky-100',        icon: Clock,       dot: 'bg-sky-500' },
  completed: { key: 'completed', color: 'text-zinc-600 bg-zinc-100',      icon: CheckCircle2, dot: 'bg-zinc-400' },
  on_hold:   { key: 'on_hold',   color: 'text-amber-700 bg-amber-100',    icon: PauseCircle, dot: 'bg-amber-500' },
  cancelled: { key: 'cancelled', color: 'text-red-700 bg-red-100',        icon: XCircle,     dot: 'bg-red-500' },
};

const durationLabel = (start, end, t) => {
  try {
    const s = parseISO(start); const e = parseISO(end);
    const days = differenceInDays(e, s) + 1;
    if (days >= 14) return `${Math.round(days / 7)} ${t('projects.weeks')}`;
    return `${days} ${t('projects.days')}`;
  } catch (_) { return ''; }
};

const daysUntil = (dateStr) => differenceInDays(parseISO(dateStr), new Date());

const ProjectCard = ({ project, users, onOpen, onEdit, onDelete, canDelete }) => {
  const { t, i18n } = useTranslation();
  const getLocale = () => (i18n.language === 'de' ? de : enUS);
  const c = COLOR_MAP[project.color] || COLOR_MAP.sky;
  const s = STATUS_META[project.computed_status] || STATUS_META.planned;
  const StatusIcon = s.icon;
  const leadUsers = (project.lead_user_ids || []).map(id => users.find(u => u.id === id) || { id, name: '?' });
  const untilStart = daysUntil(project.start_date);
  const untilEnd = daysUntil(project.end_date);
  const endingSoon = project.computed_status === 'active' && untilEnd <= 7 && untilEnd >= 0;

  return (
    <motion.div
      layout
      whileHover={{ y: -2 }}
      onClick={() => onOpen(project)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(project); } }}
      role="button"
      tabIndex={0}
      data-testid={`project-card-${project.id}`}
      className={`w-full text-left rounded-2xl border ${c.ring} ${c.bg} overflow-hidden hover:shadow-md transition-shadow relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-zinc-400`}
    >
      <div className={`h-1.5 ${c.bar}`} />
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-zinc-900 truncate flex-1">{project.title}</h3>
          <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 flex-shrink-0 ${s.color}`}>
            <StatusIcon className="w-3 h-3" />
            {t(`projects.status.${s.key}`)}
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs text-zinc-600">
          <Calendar className="w-3.5 h-3.5 text-zinc-400" />
          <span className="font-medium">
            {format(parseISO(project.start_date), 'd. MMM', { locale: getLocale() })} – {format(parseISO(project.end_date), 'd. MMM yyyy', { locale: getLocale() })}
          </span>
          <span className="text-zinc-400">·</span>
          <span className="text-zinc-500">{durationLabel(project.start_date, project.end_date, t)}</span>
        </div>

        {project.computed_status === 'active' && (
          <div>
            <div className="h-1.5 bg-white rounded-full overflow-hidden">
              <div className={`h-full ${c.bar} transition-all`} style={{ width: `${project.progress_pct}%` }} />
            </div>
            <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
              <span>{project.progress_pct}%</span>
              {endingSoon && (
                <span className="inline-flex items-center gap-1 text-amber-700 font-semibold">
                  <AlertTriangle className="w-3 h-3" />
                  {t('projects.endsInDays', { count: Math.max(0, untilEnd) })}
                </span>
              )}
            </div>
          </div>
        )}
        {project.computed_status === 'planned' && untilStart > 0 && (
          <div className="text-xs text-sky-700 font-medium">
            {t('projects.startsInDays', { count: untilStart })}
          </div>
        )}

        <div className="flex items-center justify-between">
          {leadUsers.length > 0 ? (
            <div className="flex items-center gap-1">
              <UsersIcon className="w-3.5 h-3.5 text-zinc-400" />
              <div className="flex items-center -space-x-1.5">
                {leadUsers.slice(0, 4).map(u => (
                  <UserAvatar key={u.id} user={u} size="xs" ring />
                ))}
                {leadUsers.length > 4 && (
                  <span className="w-5 h-5 rounded-full bg-zinc-300 text-zinc-700 flex items-center justify-center text-[9px] font-bold ring-2 ring-white">
                    +{leadUsers.length - 4}
                  </span>
                )}
              </div>
            </div>
          ) : <span />}
          <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit(project); }}
              className="p-1.5 rounded-lg bg-white/60 hover:bg-white shadow-sm"
              data-testid={`project-edit-${project.id}`}
              title={t('common.edit')}
            >
              <Pencil className="w-3.5 h-3.5 text-zinc-600" />
            </button>
            {canDelete && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDelete(project); }}
                className="p-1.5 rounded-lg bg-white/60 hover:bg-red-50 text-red-500 shadow-sm"
                data-testid={`project-delete-${project.id}`}
                title={t('common.delete')}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Timeline = ({ projects, users, onOpen }) => {
  const { t, i18n } = useTranslation();
  const getLocale = () => (i18n.language === 'de' ? de : enUS);
  if (!projects.length) return null;
  const starts = projects.map(p => parseISO(p.start_date));
  const ends = projects.map(p => parseISO(p.end_date));
  const rangeStart = startOfMonth(dateMin(starts));
  const rangeEnd = endOfMonth(dateMax(ends));
  const totalDays = Math.max(1, differenceInDays(rangeEnd, rangeStart));
  const today = new Date();
  const todayLeft = Math.max(0, Math.min(100, (differenceInDays(today, rangeStart) / totalDays) * 100));
  // Build monthly gridline positions
  const months = [];
  let cursor = new Date(rangeStart);
  while (cursor <= rangeEnd) {
    const left = Math.max(0, Math.min(100, (differenceInDays(cursor, rangeStart) / totalDays) * 100));
    months.push({ label: format(cursor, 'MMM yyyy', { locale: getLocale() }), left });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }

  return (
    <div className="bg-white rounded-2xl border border-zinc-200 p-4 overflow-x-auto" data-testid="projects-timeline">
      <div className="min-w-[720px]">
        {/* Header — months */}
        <div className="relative h-6 mb-2 border-b border-zinc-100">
          {months.map((m) => (
            <div key={m.label} className="absolute text-[10px] text-zinc-500 font-medium" style={{ left: `${m.left}%` }}>
              {m.label}
            </div>
          ))}
        </div>
        <div className="relative">
          {/* Today marker */}
          {todayLeft >= 0 && todayLeft <= 100 && (
            <div
              className="absolute top-0 bottom-0 w-px bg-red-400 z-10"
              style={{ left: `${todayLeft}%` }}
              title={t('projects.today')}
            >
              <span className="absolute -top-1 -translate-x-1/2 text-[10px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-bold whitespace-nowrap">
                {t('projects.today')}
              </span>
            </div>
          )}
          {/* Rows */}
          <div className="space-y-2 pt-4">
            {projects.map((p) => {
              const s = parseISO(p.start_date);
              const e = parseISO(p.end_date);
              const left = Math.max(0, Math.min(100, (differenceInDays(s, rangeStart) / totalDays) * 100));
              const width = Math.max(1, Math.min(100 - left, ((differenceInDays(e, s) + 1) / totalDays) * 100));
              const c = COLOR_MAP[p.color] || COLOR_MAP.sky;
              return (
                <div key={p.id} className="relative h-9">
                  <button
                    type="button"
                    onClick={() => onOpen(p)}
                    className={`absolute top-0 h-9 rounded-lg ${c.bar} hover:shadow-md flex items-center px-2 text-white text-xs font-semibold overflow-hidden transition-all`}
                    style={{ left: `${left}%`, width: `${width}%` }}
                    data-testid={`timeline-bar-${p.id}`}
                    title={`${p.title} · ${p.start_date} → ${p.end_date}`}
                  >
                    <span className="truncate">{p.title}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectForm = ({ initial, users, onSubmit, onCancel, saving, canDelete, onDelete }) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [startDate, setStartDate] = useState(initial?.start_date || format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(initial?.end_date || format(addDays(new Date(), 30), 'yyyy-MM-dd'));
  const [color, setColor] = useState(initial?.color || 'sky');
  const [leadIds, setLeadIds] = useState(initial?.lead_user_ids || []);
  const [manualStatus, setManualStatus] = useState(initial?.manual_status || 'auto');
  const [budgetAmount, setBudgetAmount] = useState(initial?.budget_amount == null ? '' : String(initial.budget_amount));
  const [goalAmount, setGoalAmount] = useState(initial?.goal_amount == null ? '' : String(initial.goal_amount));

  // live preview of status
  const preview = useMemo(() => {
    if (!startDate || !endDate) return null;
    const today = new Date();
    const s = parseISO(startDate); const e = parseISO(endDate);
    if (manualStatus === 'on_hold') return { status: 'on_hold' };
    if (manualStatus === 'cancelled') return { status: 'cancelled' };
    if (today < s) return { status: 'planned', days: differenceInDays(s, today) };
    if (today > e) return { status: 'completed' };
    return { status: 'active', pct: Math.round(((today - s) / (e - s)) * 100) };
  }, [startDate, endDate, manualStatus]);

  const submit = () => {
    if (!title.trim()) { toast.error(t('projects.titleRequired')); return; }
    if (parseISO(endDate) < parseISO(startDate)) { toast.error(t('projects.endBeforeStart')); return; }
    const parseAmount = (v, initialVal) => {
      const trimmed = String(v ?? '').trim();
      if (trimmed === '') {
        // If editing and initial had a value → -1 to clear on server, else null (no change / not set)
        return initial && initialVal != null ? -1 : null;
      }
      const n = parseFloat(trimmed.replace(',', '.'));
      return isNaN(n) || n < 0 ? null : n;
    };
    onSubmit({
      title: title.trim(),
      description: description.trim() || null,
      start_date: startDate,
      end_date: endDate,
      color,
      lead_user_ids: leadIds,
      manual_status: (manualStatus === 'on_hold' || manualStatus === 'cancelled') ? manualStatus : '',
      budget_amount: parseAmount(budgetAmount, initial?.budget_amount),
      goal_amount: parseAmount(goalAmount, initial?.goal_amount),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium">{t('projects.formTitle')}</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 h-11" data-testid="project-title-input" autoFocus />
      </div>
      <div>
        <Label className="text-sm font-medium">{t('projects.formDescription')}</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1.5 resize-none" data-testid="project-description-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-medium">{t('projects.startDate')}</Label>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5 h-11" data-testid="project-start-input" />
        </div>
        <div>
          <Label className="text-sm font-medium">{t('projects.endDate')}</Label>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5 h-11" data-testid="project-end-input" />
        </div>
      </div>

      {preview && (
        <div className="text-xs bg-zinc-50 border border-zinc-200 rounded-lg p-2 text-zinc-600">
          {preview.status === 'active' && t('projects.previewActive', { pct: preview.pct })}
          {preview.status === 'planned' && t('projects.previewPlanned', { count: preview.days })}
          {preview.status === 'completed' && t('projects.previewCompleted')}
          {preview.status === 'on_hold' && t('projects.previewOnHold')}
          {preview.status === 'cancelled' && t('projects.previewCancelled')}
        </div>
      )}

      <div>
        <Label className="text-sm font-medium">{t('projects.color')}</Label>
        <div className="flex gap-2 mt-1.5">
          {Object.entries(COLOR_MAP).map(([id, c]) => (
            <button
              key={id} type="button" onClick={() => setColor(id)} data-testid={`project-color-${id}`}
              className={`w-8 h-8 rounded-full ${c.bar} ${color === id ? 'ring-2 ring-offset-2 ring-zinc-900' : ''}`}
              title={id}
            />
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">{t('projects.leads')}</Label>
        <div className="mt-1.5">
          <MultiUserSelect users={users} value={leadIds} onChange={setLeadIds} placeholder={t('projects.leadsPlaceholder')} />
        </div>
      </div>

      <div>
        <Label className="text-sm font-medium">{t('projects.manualStatus')}</Label>
        <Select value={manualStatus} onValueChange={setManualStatus}>
          <SelectTrigger className="mt-1.5 h-11" data-testid="project-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="auto">{t('projects.autoStatus')}</SelectItem>
            <SelectItem value="on_hold">{t('projects.status.on_hold')}</SelectItem>
            <SelectItem value="cancelled">{t('projects.status.cancelled')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-medium">{t('projects.budgetAmount')}</Label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder={t('projects.budgetPh')}
              className="pl-7 h-11"
              data-testid="project-budget-input"
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">{t('projects.budgetHint')}</p>
        </div>
        <div>
          <Label className="text-sm font-medium">{t('projects.goalAmount')}</Label>
          <div className="relative mt-1.5">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">€</span>
            <Input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={goalAmount}
              onChange={(e) => setGoalAmount(e.target.value)}
              placeholder={t('projects.goalPh')}
              className="pl-7 h-11"
              data-testid="project-goal-input"
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">{t('projects.goalHint')}</p>
        </div>
      </div>

      <DialogFooter className="gap-2 pt-2 sm:justify-between">
        {canDelete ? (
          <Button variant="outline" onClick={onDelete} className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="project-form-delete-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> {t('common.delete')}
          </Button>
        ) : <span />}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} className="rounded-full">{t('common.cancel')}</Button>
          <Button onClick={submit} disabled={saving} data-testid="project-form-save-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full">
            {saving ? t('common.loading') : t('common.save')}
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
};

const ProjectsPage = () => {
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [view, setView] = useState('cards');
  const [showCompleted, setShowCompleted] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const getLocale = () => (i18n.language === 'de' ? de : enUS);

  const fetchProjects = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      const r = await axios.get(`${API_URL}/projects?${params.toString()}`, { headers: getAuthHeader() });
      setProjects(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  }, [getAuthHeader, search, t]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  }, [getAuthHeader, t]);

  useEffect(() => { fetchProjects(); fetchUsers(); }, [fetchProjects, fetchUsers]);

  const groups = useMemo(() => {
    const q = statusFilter === 'all' ? null : statusFilter;
    const list = projects.filter(p => !q || p.computed_status === q);
    return {
      active: list.filter(p => p.computed_status === 'active').sort((a, b) => a.end_date.localeCompare(b.end_date)),
      planned: list.filter(p => p.computed_status === 'planned').sort((a, b) => a.start_date.localeCompare(b.start_date)),
      completed: list.filter(p => p.computed_status === 'completed').sort((a, b) => b.end_date.localeCompare(a.end_date)),
      other: list.filter(p => ['on_hold', 'cancelled'].includes(p.computed_status)),
    };
  }, [projects, statusFilter]);

  const counters = useMemo(() => ({
    active: projects.filter(p => p.computed_status === 'active').length,
    planned: projects.filter(p => p.computed_status === 'planned').length,
    completed: projects.filter(p => p.computed_status === 'completed').length,
  }), [projects]);

  const openCreate = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setDialogOpen(true); };
  const openDetail = (p) => navigate(`/projects/${p.id}`);

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/projects/${editing.id}`, payload, { headers: getAuthHeader() });
        toast.success(t('projects.updated'));
      } else {
        await axios.post(`${API_URL}/projects`, payload, { headers: getAuthHeader() });
        toast.success(t('projects.created'));
      }
      setDialogOpen(false); setEditing(null);
      fetchProjects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    } finally { setSaving(false); }
  };

  const handleDelete = async (project) => {
    if (!window.confirm(t('projects.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/projects/${project.id}`, { headers: getAuthHeader() });
      toast.success(t('projects.deleted'));
      setDialogOpen(false); setEditing(null);
      fetchProjects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('projects.error'));
    }
  };

  const canDelete = (p) => p.created_by === user?.id || user?.role === 'admin';
  const timelineProjects = projects.filter(p => showCompleted || p.computed_status !== 'completed');

  return (
    <Layout>
      <div data-testid="projects-page" className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t('projects.title')}
            </h1>
            <p className="text-zinc-600 text-sm mt-1">{t('projects.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center rounded-full bg-zinc-100 p-1">
              <button
                type="button"
                onClick={() => setView('cards')}
                data-testid="view-cards-btn"
                className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${view === 'cards' ? 'bg-white shadow text-zinc-900' : 'text-zinc-500'}`}
              >
                <LayoutGrid className="w-4 h-4" /> {t('projects.cards')}
              </button>
              <button
                type="button"
                onClick={() => setView('timeline')}
                data-testid="view-timeline-btn"
                className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${view === 'timeline' ? 'bg-white shadow text-zinc-900' : 'text-zinc-500'}`}
              >
                <GanttChart className="w-4 h-4" /> {t('projects.timeline')}
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTemplatesOpen(true)}
              className="rounded-full h-10 border-zinc-200 hover:border-zinc-300"
              data-testid="templates-open-btn"
              title={t('templates.title')}
            >
              <Settings2 className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">{t('templates.buttonLabel')}</span>
            </Button>
            <Button onClick={openCreate} data-testid="project-add-btn" className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full h-10">
              <Plus className="w-4 h-4 mr-1.5" /> {t('projects.newProject')}
            </Button>
          </div>
        </div>

        {/* Counter chips */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="projects-counters">
          <CounterChip active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label={t('projects.filters.all')} count={projects.length} dot="bg-zinc-400" />
          <CounterChip active={statusFilter === 'active'} onClick={() => setStatusFilter('active')} label={t('projects.status.active')} count={counters.active} dot="bg-emerald-500" />
          <CounterChip active={statusFilter === 'planned'} onClick={() => setStatusFilter('planned')} label={t('projects.status.planned')} count={counters.planned} dot="bg-sky-500" />
          <CounterChip active={statusFilter === 'completed'} onClick={() => setStatusFilter('completed')} label={t('projects.status.completed')} count={counters.completed} dot="bg-zinc-400" />
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl border border-zinc-200 flex items-center gap-2 px-3">
          <Search className="w-4 h-4 text-zinc-400 flex-shrink-0" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('projects.searchPlaceholder')}
            className="border-0 shadow-none focus-visible:ring-0 h-11 px-0"
            data-testid="projects-search"
          />
        </div>

        {view === 'timeline' ? (
          timelineProjects.length === 0 ? (
            <EmptyState onClick={openCreate} />
          ) : (
            <Timeline projects={timelineProjects} users={users} onOpen={openDetail} />
          )
        ) : (
          <div className="space-y-8">
            <Section
              title={t('projects.sections.active')}
              icon={Play}
              items={groups.active}
              renderItem={(p) => <ProjectCard key={p.id} project={p} users={users} onOpen={openDetail} onEdit={openEdit} onDelete={handleDelete} canDelete={canDelete(p)} />}
              empty={t('projects.emptyActive')}
            />
            <Section
              title={t('projects.sections.planned')}
              icon={Clock}
              items={groups.planned}
              renderItem={(p) => <ProjectCard key={p.id} project={p} users={users} onOpen={openDetail} onEdit={openEdit} onDelete={handleDelete} canDelete={canDelete(p)} />}
              empty={t('projects.emptyPlanned')}
            />
            {groups.other.length > 0 && (
              <Section
                title={t('projects.sections.other')}
                icon={PauseCircle}
                items={groups.other}
                renderItem={(p) => <ProjectCard key={p.id} project={p} users={users} onOpen={openDetail} onEdit={openEdit} onDelete={handleDelete} canDelete={canDelete(p)} />}
              />
            )}
            <CollapsibleSection
              title={t('projects.sections.completed')}
              count={groups.completed.length}
              open={showCompleted}
              onToggle={() => setShowCompleted(v => !v)}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {groups.completed.map(p => (
                  <ProjectCard key={p.id} project={p} users={users} onOpen={openDetail} onEdit={openEdit} onDelete={handleDelete} canDelete={canDelete(p)} />
                ))}
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>

      {/* Create/edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="project-form-desc">
          <DialogHeader>
            <DialogTitle>{editing ? t('projects.editProject') : t('projects.newProject')}</DialogTitle>
            <p id="project-form-desc" className="sr-only">{t('projects.title')}</p>
          </DialogHeader>
          <ProjectForm
            key={editing?.id || 'new'}
            initial={editing}
            users={users}
            saving={saving}
            canDelete={!!editing && canDelete(editing)}
            onSubmit={handleSubmit}
            onCancel={() => { setDialogOpen(false); setEditing(null); }}
            onDelete={() => handleDelete(editing)}
          />
        </DialogContent>
      </Dialog>

      {/* Templates management dialog */}
      <ProjectTaskTemplatesDialog
        open={templatesOpen}
        onOpenChange={setTemplatesOpen}
      />
    </Layout>
  );
};

const CounterChip = ({ active, label, count, dot, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`counter-${label}`}
    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
      active ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
    }`}
  >
    <span className={`w-2 h-2 rounded-full ${dot}`} />
    <span>{label}</span>
    <span className={`px-1.5 rounded-full text-[10px] ${active ? 'bg-white/20' : 'bg-zinc-100'}`}>{count}</span>
  </button>
);

const Section = ({ title, icon: Icon, items, renderItem, empty }) => (
  <section>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-zinc-500" />
      <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-700">{title}</h2>
      <span className="text-xs text-zinc-400">({items.length})</span>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-zinc-400 py-4 pl-6">{empty}</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        <AnimatePresence>
          {items.map(renderItem)}
        </AnimatePresence>
      </div>
    )}
  </section>
);

const CollapsibleSection = ({ title, count, open, onToggle, children }) => (
  <section>
    <button
      type="button"
      onClick={onToggle}
      data-testid="collapse-completed"
      className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity"
    >
      {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
      <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-700">{title}</h2>
      <span className="text-xs text-zinc-400">({count})</span>
    </button>
    {open && children}
  </section>
);

const EmptyState = ({ onClick }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-2xl border-2 border-dashed border-zinc-200 py-12 text-center">
      <Calendar className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
      <p className="text-zinc-500 text-sm mb-4">{t('projects.emptyState')}</p>
      <Button onClick={onClick} className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full">
        <Plus className="w-4 h-4 mr-1.5" /> {t('projects.newProject')}
      </Button>
    </div>
  );
};

export default ProjectsPage;
